/**
 * lib/adjustments.ts
 * ------------------------------------------------------------------
 * CREDIT NOTES, WRITE-OFFS AND REFUNDS — the three ways a debtor balance goes
 * down without money coming in.
 *
 * Until now the only way to reduce a pupil's account was to void the invoice or
 * hand-write a journal. Voiding destroys the history of what was billed;
 * a manual journal leaves the sub-ledger and the GL disagreeing, because the
 * invoice still says it is owed. Neither is an auditable adjustment.
 *
 * ONE DOCUMENT TYPE, THREE REASONS. All three credit the debtor; they differ
 * only in which account absorbs it, and that difference is the whole point of
 * distinguishing them:
 *
 *   credit_note  Dr an income account   — we over-billed. Revenue was never
 *                really earned, so it comes back out of income.
 *   write_off    Dr BAD_DEBTS           — the charge was correct and we will
 *                not collect it. That is a COST of doing business, not a
 *                reduction in revenue, and conflating the two flatters the
 *                fee-yield figure the school manages by.
 *   refund       Dr AR_FEES / Cr bank   — the pupil overpaid and we are giving
 *                cash back. The only one of the three that moves money, so it
 *                raises a payment VOUCHER rather than touching income at all.
 *
 * Every one is APPEND-ONLY, like a journal: an adjustment is corrected by
 * another adjustment, never edited. The invoice keeps its original total and
 * carries the adjustment against it, so "what did we bill" and "what did we
 * settle for" are both still answerable a year later.
 *
 * PURE: types, numbering and the posting lines. No Firestore, no React.
 * ------------------------------------------------------------------ */
/**
 * A single side of a journal entry.
 *
 * Declared HERE rather than imported from lib/gl, which does not exist yet —
 * the ledger is block 3. An adjustment already knows which accounts it hits,
 * and that knowledge is worth capturing while it is being written rather than
 * reconstructed later; nothing posts these lines until the ledger arrives.
 *
 * MOVE THIS to lib/gl when that lands, and import it from there. Two
 * definitions of a journal line is how the two halves of a posting end up
 * disagreeing about what a line is.
 */
export interface JournalLine {
  accountCode: string;
  dr: number;
  cr: number;
  narration?: string;
}

import type { DiscountKind } from '@/lib/financeCore';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export const ADJUSTMENT_COLLECTION = 'financeAdjustments';

export type AdjustmentKind = 'credit_note' | 'write_off' | 'refund';

export const ADJUSTMENT_LABEL: Record<AdjustmentKind, string> = {
  credit_note: 'Credit note',
  write_off: 'Bad-debt write-off',
  refund: 'Refund',
};

/** What each kind is FOR, in the words a bursar would use. Shown on the form so
 *  the choice is made on meaning rather than on which button is nearest. */
export const ADJUSTMENT_HELP: Record<AdjustmentKind, string> = {
  credit_note: 'We billed too much, or billed for something not delivered. Reverses the charge out of income.',
  write_off: 'The charge was right but will not be collected. Goes to bad debts as a cost, leaving revenue intact.',
  refund: 'The family overpaid and money is going back to them. Raises a payment voucher.',
};

export interface Adjustment {
  id: string;
  /** ADJ/2026/0001 — its own gapless series, never an invoice number. */
  adjustmentNo: string;
  kind: AdjustmentKind;
  studentId: string;
  studentName?: string;
  familyId?: string;
  /** The invoice being adjusted. Optional for a refund of a general credit. */
  invoiceId?: string;
  invoiceNo?: string;
  fy: string;
  /** Positive amount. Direction is implied by the kind, never by a sign — a
   *  negative "amount" is the classic way a credit becomes a charge. */
  amount: number;
  reason: string;
  /** credit_note: the income account to reverse out of. */
  incomeAccountCode?: string;
  /** System key of the income account this reverses out of, e.g.
   *  DISCOUNTS_GRANTED for a concession. Preferred over incomeAccountCode:
   *  a key survives the school renumbering its chart. */
  incomeAccountKey?: string;
  /** refund: the voucher raised to pay it. */
  voucherId?: string;
  date: string;
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  /** A discount issued like a receipt (Nov 2026): a credit note to 4900 in its
   *  own DSC/ number series. Its reversal carries the flag too. */
  isDiscount?: boolean;
  discountKind?: DiscountKind;
  /** Set when a later adjustment reverses this one. */
  reversedBy?: string;
  reversesId?: string;
}

/** What to call it: a discount is a credit note underneath, not to its reader. */
export function adjustmentLabel(a: Pick<Adjustment, 'kind' | 'isDiscount'>): string {
  return a.isDiscount ? 'Discount' : ADJUSTMENT_LABEL[a.kind];
}

export const discountCounterId = (year: string): string => `discounts-${year}`;
export const formatDiscountNo = (seq: number, year: string): string =>
  `DSC/${year}/${String(seq).padStart(4, '0')}`;

/**
 * The most a discount can take off an invoice: the fee left on it. It may go
 * past what is still OWED, because the school wants a paid invoice discountable,
 * and that excess becomes credit on the account. It may not go past the FEE: a
 * family cannot be discounted more than they were billed.
 */
export function discountRoom(invoice: { total: number; adjusted?: number }): number {
  return Math.max(0, round2((Number(invoice.total) || 0) - (Number(invoice.adjusted) || 0)));
}

/** What a discount comes to. A percentage is of the invoice's fee. */
export function discountAmount(mode: 'percent' | 'amount', value: number, total: number): number {
  const v = Number(value) || 0;
  return mode === 'percent' ? round2(((Number(total) || 0) * v) / 100) : round2(v);
}

/** How much of a discount lands as credit on the account, past what was owed. */
export function discountToCredit(amount: number, balance: number): number {
  return Math.max(0, round2(round2(amount) - Math.max(0, round2(balance))));
}

export const adjustmentCounterId = (year: string): string => `adjustments-${year}`;
export const formatAdjustmentNo = (seq: number, year: string): string =>
  `ADJ/${year}/${String(seq).padStart(4, '0')}`;

/** An amount must be a positive number — see the note on `amount`. */
export function assertAdjustmentAmount(amount: number): number {
  const a = round2(amount);
  if (!(a > 0)) {
    throw new Error('An adjustment must be for a positive amount. The kind decides the direction.');
  }
  return a;
}

export interface AdjustmentAccounts {
  arFees: string;
  badDebts: string;
  /** Only needed for a credit note. */
  income?: string;
}

/**
 * The journal for an adjustment.
 *
 * Credit notes and write-offs both CREDIT receivables — the pupil owes less —
 * and differ only in the debit. A refund posts nothing here: it is settled by a
 * payment voucher, and posting both would double-count the cash.
 */
export function adjustmentLines(
  adj: Pick<Adjustment, 'kind' | 'amount' | 'adjustmentNo' | 'invoiceNo' | 'studentName' | 'reversesId'>,
  accounts: AdjustmentAccounts,
): JournalLine[] {
  const amount = assertAdjustmentAmount(adj.amount);
  const who = [adj.invoiceNo, adj.studentName].filter(Boolean).join(' ');

  if (adj.kind === 'refund') {
    // Deliberately empty: the voucher's own posting path moves the money.
    return [];
  }

  const debit = adj.kind === 'write_off' ? accounts.badDebts : accounts.income;
  if (!debit) {
    throw new Error(
      adj.kind === 'write_off'
        ? 'No account carries the system key BAD_DEBTS. Set it in Chart of Accounts.'
        : 'Choose the income account this credit note reverses out of.',
    );
  }

  /* A REVERSAL IS THE SAME TWO ACCOUNTS WITH THE SIDES SWAPPED. It carries a
     positive amount like the original, so posted the same way round it would
     take the money off twice instead of putting it back. */
  const back = !!adj.reversesId;
  return [
    {
      accountCode: debit,
      dr: back ? 0 : amount,
      cr: back ? amount : 0,
      narration: `${ADJUSTMENT_LABEL[adj.kind]} ${adj.adjustmentNo}${who ? ` — ${who}` : ''}`,
    },
    {
      accountCode: accounts.arFees,
      dr: back ? amount : 0,
      cr: back ? 0 : amount,
      narration: `${adj.adjustmentNo}${who ? ` — ${who}` : ''}`,
    },
  ];
}

/**
 * How much of an invoice may still be adjusted.
 *
 * You cannot credit or write off more than is actually outstanding: doing so
 * would push the invoice into a negative balance, which reads as the school
 * owing the family money it never received. A genuine overpayment is a REFUND,
 * which is why that is a separate kind.
 */
export function adjustableRemaining(
  invoice: { total: number; paid: number },
  alreadyAdjusted: number,
): number {
  const bal = round2((Number(invoice.total) || 0) - (Number(invoice.paid) || 0));
  return Math.max(0, round2(bal - round2(alreadyAdjusted)));
}

/** Sum the adjustments that reduce a balance (credit notes + write-offs).
 *  Refunds are excluded: they follow an overpayment, so they do not reduce what
 *  was owed — subtracting them would understate the debt. */
export function balanceReducingTotal(adjustments: Adjustment[]): number {
  return round2(adjustments
    .filter((a) => a.kind !== 'refund' && !a.reversedBy && !a.reversesId)
    .reduce((n, a) => n + (Number(a.amount) || 0), 0));
}
